import { type ReactElement, useCallback, useState } from 'react';
import { Alert, Box } from 'components/ui';
import { SUMMARY_DASHBOARD_TEST_IDS } from '../../SummaryDashboard.constants';
import { DASHBOARD_PROMPT_TEMPLATES } from '../../templates';
import DashboardPromptInput from '../DashboardPromptInput';
import DashboardTemplateTile from '../DashboardTemplateTile';
import { DASHBOARD_PROMPT_MAX_ROWS } from './DashboardPromptStarter.constants';
import type { DashboardPromptStarterProps } from './DashboardPromptStarter.props';
import { starterErrorSx, starterRootSx, templateRowSx } from './DashboardPromptStarter.styles';
import { useTemplateRowVisibility } from './useTemplateRowVisibility';
import {
  DASHBOARD_PROMPT_BUSY_HINT,
  DASHBOARD_PROMPT_PLACEHOLDER,
  DASHBOARD_PROMPT_SUBMIT_HINT,
  DASHBOARD_PROMPT_SUBMIT_LABEL,
} from '../DashboardPromptInput/DashboardPromptInput.constants';

const PROMPT_INPUT_TEST_IDS = {
  input: SUMMARY_DASHBOARD_TEST_IDS.promptInput,
  submitButton: SUMMARY_DASHBOARD_TEST_IDS.promptSubmitButton,
  submitHint: SUMMARY_DASHBOARD_TEST_IDS.promptSubmitHint,
};

const INITIAL_SEED = { prompt: '', token: 0 };

// Empty state for a dashboard-kind summary tab: the AI prompt box plus the template gallery.
// Submitting generates a real dashboard through the AI endpoint and the owner persists it. Clicking a
// template only loads its prompt into the box — the user still reads, edits and submits it, because a
// template is a starting point for a brief rather than a preset dashboard.
const DashboardPromptStarter = ({
  errorMessage,
  isGenerating = false,
  onSubmit,
}: DashboardPromptStarterProps): ReactElement => {
  const { isTemplateRowVisible, promptInputRef } = useTemplateRowVisibility();

  // DashboardPromptInput seeds from `initialValue` on mount only, so a template is applied by
  // remounting it — the same mechanism DashboardPromptBar uses to re-seed on expand. The token is
  // what forces that: it remounts even when the text is unchanged, so clicking the same tile twice
  // reloads its prompt over whatever was typed in between.
  const [seed, setSeed] = useState(INITIAL_SEED);

  const applyTemplate = useCallback((prompt: string) => {
    setSeed(previous => ({ prompt, token: previous.token + 1 }));
  }, []);

  return (
    <Box data-testid={SUMMARY_DASHBOARD_TEST_IDS.emptyState} sx={starterRootSx}>
      <DashboardPromptInput
        // Not on first mount: opening the tab must not steal focus. Once a template has been applied,
        // the remount is exactly when the caret should land in the box.
        autoFocus={seed.token > 0}
        busyHint={DASHBOARD_PROMPT_BUSY_HINT}
        initialValue={seed.prompt}
        inputRef={promptInputRef}
        isBusy={isGenerating}
        key={seed.token}
        maxRows={DASHBOARD_PROMPT_MAX_ROWS}
        onSubmit={onSubmit}
        placeholder={DASHBOARD_PROMPT_PLACEHOLDER}
        submitHint={DASHBOARD_PROMPT_SUBMIT_HINT}
        submitLabel={DASHBOARD_PROMPT_SUBMIT_LABEL}
        testIds={PROMPT_INPUT_TEST_IDS}
      />
      {Boolean(errorMessage) && (
        <Alert data-testid={SUMMARY_DASHBOARD_TEST_IDS.promptError} severity="error" sx={starterErrorSx}>
          {errorMessage}
        </Alert>
      )}
      {isTemplateRowVisible && (
        <Box data-testid={SUMMARY_DASHBOARD_TEST_IDS.templateRow} sx={templateRowSx}>
          {DASHBOARD_PROMPT_TEMPLATES.map(template => (
            // Blocked while generating: applying a template remounts the prompt input, which would
            // replace the brief the in-flight request was made from with the template's text.
            <DashboardTemplateTile
              isDisabled={isGenerating}
              key={template.id}
              onApply={applyTemplate}
              template={template}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default DashboardPromptStarter;
